import React, { useEffect, useMemo, useState } from "react";
import PricingItemCard from "./PricingItemCard";
import CustomButton from "components/common/Button/CustomButton";
import { CheckCircleIcon } from "@heroicons/react/outline";
import { BsDash } from "react-icons/bs";
import { useSelector } from "react-redux";
import { getStripeSaveCardDataLoader, getXeroSubscriptionData } from "store/selector/sp.selector";
import useDefaultPaymentMethod from "hooks/useDefaultPaymentMethod";
import { useNavigate } from "react-router";
import { classNames } from "Utils/utils";
import CheckoutWrapper from "pages/Stripe/checkout/index";
import {
  BILLING_BREADCRUMB,
  BILLING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import Breadcrumb from "components/common/Breadcrumb";
import { getUser } from "store/selector/auth.selector";
import { XERO_APP_ID } from "store/constants";
import { toast } from "react-toastify";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import api from "store/services/sp.services";

/** Parse participant tier label to max participant count for that tier. */
function getTierMaxParticipantCount(label) {
  if (!label || typeof label !== "string") return 0;
  const trimmed = label.trim();
  if (trimmed.includes("200+")) return 999;
  const match = trimmed.match(/(\d+)\s*to\s*(\d+)/i);
  if (match) return Math.max(parseInt(match[1], 10), parseInt(match[2], 10));
  return 0;
}

const PricingTableNew = (props) => {
  const {
    data,
    paymentSettings,
    isCurrentSubscription = null,
    isTrialChoosePlanBtnClick,
    isShowXeroButton,
    currentParticipantCount: currentParticipantCountProp,
  } = props;

  const currentParticipantCount =
    Number(currentParticipantCountProp) ||
    Number(paymentSettings?.current_participant_count) ||
    0;

  const getXeroSubscription=useSelector(getXeroSubscriptionData);


  const PLAN_TYPE = {
    TOTAL_SUITE: "total_suite",
    ELEVATED: "elevated",
    PLATINUM_PLAN: "platinum_plan",
  };

  const resolvePlanTypeFromName = (planName) => {
    const normalized = (planName || "").toLowerCase();
    if (normalized.includes("platinum")) {
      return PLAN_TYPE.PLATINUM_PLAN;
    }
    if (normalized.includes("elevated")) {
      return PLAN_TYPE.ELEVATED;
    }
    return PLAN_TYPE.TOTAL_SUITE;
  };

  const getPlanTypeLabel = (planType) => {
    if (planType === PLAN_TYPE.PLATINUM_PLAN) return "Platinum Plan";
    if (planType === PLAN_TYPE.ELEVATED) return "Elevated";
    return "Total Suite";
  };

  const saveCardDataLoader = useSelector(getStripeSaveCardDataLoader);

  const { getDefaultCardDetails, stripeDefaultPaymentMethodLaoder } =
    useDefaultPaymentMethod();

  const defaultCardDetails = useMemo(
    () => getDefaultCardDetails,
    [getDefaultCardDetails, paymentSettings, stripeDefaultPaymentMethodLaoder, saveCardDataLoader]
  );

  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedParticipants, setSelectedParticipants] = useState(null);
  const navigate = useNavigate();

  const [checkoutPage, setCheckoutPage] = useState(false);
  const [confirmPlanModalOpen, setConfirmPlanModalOpen] = useState(false);
  const [currentSelectedPlan, setCurrentSelectedPlan] = useState(
    PLAN_TYPE.TOTAL_SUITE
  );
  const [onboardingFee, setOnboardingFee] = useState(null);
  const [onboardingFeeLoading, setOnboardingFeeLoading] = useState(false);


  useEffect(() => {
    if (isTrialChoosePlanBtnClick !== 0) {
      setCheckoutPage(false);
    }
  }, [isTrialChoosePlanBtnClick]);

  useEffect(() => {
    if (isCurrentSubscription !== null && isCurrentSubscription?.id) {
      setCurrentSelectedPlan(
        resolvePlanTypeFromName(isCurrentSubscription?.subscriptionPlan)
      );
      if (isCurrentSubscription.noOfParticipants?.replace(":", "")) {
        const tier = isCurrentSubscription.noOfParticipants?.replace(":", "");
        setSelectedParticipants(tier);
        // Do not set selectedPrice to current plan — user must choose a different plan to proceed
        setSelectedPrice(null);
      }
    }
  }, [isCurrentSubscription]);

  useEffect(()=>{
if(getXeroSubscription?.status === "success"){
  toast.success(getXeroSubscription?.message ||"Xero subscription created successfully")
navigate("/subscription")
}
},[getXeroSubscription]);

  /** True when user has an active subscription (upgrading = no onboarding fee). */
  const hasCurrentSubscription =
    isCurrentSubscription != null && isCurrentSubscription?.id;

  /** Fetch onboarding fee only for new subscriptions when confirm modal is open. */
  useEffect(() => {
    const priceId = selectedPrice?.[selectedParticipants]?.price_id;
    if (!confirmPlanModalOpen || !priceId || hasCurrentSubscription) {
      setOnboardingFee(null);
      setOnboardingFeeLoading(false);
      return;
    }
    let cancelled = false;
    setOnboardingFeeLoading(true);
    api
      .stripeOnboardingFee(priceId)
      .then((res) => {
        if (cancelled) return;
        const fee = res?.onboarding_fee ?? res?.data?.onboarding_fee ?? null;
        setOnboardingFee(fee);
        setOnboardingFeeLoading(false);
      })
      .catch(() => {
        if (!cancelled) setOnboardingFee(null);
        setOnboardingFeeLoading(false);
      });
    return () => { cancelled = true; };
  }, [confirmPlanModalOpen, selectedPrice, selectedParticipants, hasCurrentSubscription]);
  const features = {
    Invoicing: [
      {
        value: "Effortless Invoicing",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Built in NDIS Price Catalogue",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Streamlined Process",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Conveniently Email Invoices",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Versatile Output Options",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Improved Accuracy",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
    ],
    "Digital Participant Support Plans": [
      {
        value: "Complete Digital Participant Onboarding",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Participant Listing",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Participant Support Plans",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Feedback Register",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Appointment Setting",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Document Management System",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Funding Management",
        isBasicPlan: false,
        isPlatinumPlan: true,
      },
      {
        value: "Participant NDIS Compliance",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
    ],
    Rostering: [
      {
        value: "One Simple Screen for All Your Participants and Workers",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Easily Add New Shifts",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Copy and Recurring Shifts",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Task List for Support Workers",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Group Shift Rostering",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Worker Tracking With Welfare Check via Mobile Application",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Participant Signature via Mobile Application",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Payroll Integration (Xero, MYOB, QuickBooks)",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "NDIS Compliance",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
    ],
    HRM: [
      {
        value: "Applicant Tracking System",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Worker Onboarding",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Worker Web Portal",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Staff Skill Metrics and Traits",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Staff Training Registers",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Document Expiry Reminders ",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Employee's Performance Review ",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Birthday and Work Anniversary Greetings",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Deactivating Worker ",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
    ],
    "Plan Features": [
      {
        value: "Family Login",
        total_suite: false,
        elevated: true,
        platinum_plan: true,
      },
      {
        value: "In-App Chat",
        total_suite: false,
        elevated: true,
        platinum_plan: true,
      },
      {
        value: "Regions",
        total_suite: false,
        elevated: true,
        platinum_plan: true,
      },
      {
        value: "Roles & Permissions (limit)",
        total_suite: "5 Primary + 5 Frontline",
        elevated: "Unlimited",
        platinum_plan: "Unlimited",
      },
    ],
    "Risk Management": [
      {
        value: "Participant Risk Assessment",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Risk Mitigation Planning",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Document Management System",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "NDIS Workers Compliance",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
      {
        value: "Risk Ratings",
        isBasicPlan: true,
        isPlatinumPlan: true,
      },
    ],
  };

  const handleCloseCheckoutPage = () => {
    setSelectedParticipants(null);
    // setSelectedPlan(null);
    setSelectedPrice(null);
    setCheckoutPage(false);
    navigate("/subscription");
  };

  useEffect(() => {
    if (isCurrentSubscription === null) {
      setSelectedPrice(null);
    }
  }, [currentSelectedPlan]);

  const pricingTableByplan = useMemo(() => {
    if (currentSelectedPlan && data?.length > 0) {
      const structuredData = data.reduce((acc, item) => {
        const participants = item.name;
        const planPricing = item[currentSelectedPlan];
        if (!planPricing) {
          return acc;
        }

        const year1 = planPricing["1_year"];
        const year2 = planPricing["2_year"];
        const yearly = planPricing["yearly"];

        if (!acc[currentSelectedPlan]) {
          acc[currentSelectedPlan] = {
            participants: [],
            year_1: {},
            year_2: {},
            yearly:{}
          };
        }

        acc[currentSelectedPlan].participants.push(participants);
        acc[currentSelectedPlan].year_1[participants] = year1;
        acc[currentSelectedPlan].year_2[participants] = year2;
        acc[currentSelectedPlan].yearly[participants] = yearly;

        return acc;
      }, {});
      return structuredData?.[currentSelectedPlan];
    }
    return null;
  }, [data, currentSelectedPlan]);

  /** Current plan's tier max (e.g. 20 for "11 to 20 Participants"). Used to disable lower tiers (no downgrade). */
  const currentPlanTierMax = useMemo(() => {
    if (!isCurrentSubscription?.id || !isCurrentSubscription?.noOfParticipants) return null;
    const label = isCurrentSubscription.noOfParticipants.replace(":", "").trim();
    return getTierMaxParticipantCount(label) || null;
  }, [isCurrentSubscription?.id, isCurrentSubscription?.noOfParticipants]);

  /** Tiers that are lower than the current plan — downgrades are not allowed. */
  const downgradeDisabledTiers = useMemo(() => {
    if (currentPlanTierMax == null || currentPlanTierMax <= 0 || !pricingTableByplan?.participants?.length) return new Set();
    const set = new Set();
    pricingTableByplan.participants.forEach((label) => {
      const priceObj = pricingTableByplan.year_1?.[label] || pricingTableByplan.yearly?.[label];
      const tierMax = priceObj?.participant_max != null ? Number(priceObj.participant_max) : getTierMaxParticipantCount(label);
      if (tierMax < currentPlanTierMax) set.add(label);
    });
    return set;
  }, [currentPlanTierMax, pricingTableByplan]);

  /** Tiers that cannot be selected: usage exceeds tier max, or tier is a downgrade. */
  const disabledParticipantTiers = useMemo(() => {
    const set = new Set();
    if (pricingTableByplan?.participants?.length) {
      pricingTableByplan.participants.forEach((label) => {
        const priceObj = pricingTableByplan.year_1?.[label] || pricingTableByplan.yearly?.[label];
        const tierMax = priceObj?.participant_max != null ? Number(priceObj.participant_max) : getTierMaxParticipantCount(label);
        if (currentParticipantCount > 0 && tierMax < currentParticipantCount) set.add(label);
      });
    }
    downgradeDisabledTiers.forEach((label) => set.add(label));
    return set;
  }, [currentParticipantCount, pricingTableByplan, downgradeDisabledTiers]);

  /** Summary of selected plan for the confirmation modal. */
  const selectedPlanSummary = useMemo(() => {
    if (!selectedParticipants || !selectedPrice?.[selectedParticipants] || !pricingTableByplan) return null;
    const priceObj = selectedPrice[selectedParticipants];
    const isAnnual =
      pricingTableByplan.yearly?.[selectedParticipants]?.price_id === priceObj?.price_id;
    const monthlyRow = pricingTableByplan.year_1?.[selectedParticipants];
    const yearlyRow = pricingTableByplan.yearly?.[selectedParticipants];
    const planTypeLabel = getPlanTypeLabel(currentSelectedPlan);
    const billingLabel = isAnnual ? "Annually" : "Monthly";
    const priceDisplay = isAnnual
      ? yearlyRow?.monthly ?? `${priceObj?.unit_amount ?? ""} Plus GST/year`
      : monthlyRow?.monthly ?? `${priceObj?.unit_amount ?? ""} Plus GST/month`;
    return {
      planTypeLabel,
      participantTier: selectedParticipants,
      billingLabel,
      priceDisplay,
    };
  }, [
    selectedParticipants,
    selectedPrice,
    pricingTableByplan,
    currentSelectedPlan,
  ]);

  /** When user has no subscription but has participants, auto-select the minimum valid tier. */
  useEffect(() => {
    if (
      isCurrentSubscription !== null ||
      currentParticipantCount <= 0 ||
      !pricingTableByplan?.participants?.length
    ) return;
    const firstValidTier = pricingTableByplan.participants.find((label) => {
      const priceObj = pricingTableByplan.year_1?.[label] || pricingTableByplan.yearly?.[label];
      const tierMax = priceObj?.participant_max != null ? Number(priceObj.participant_max) : getTierMaxParticipantCount(label);
      return tierMax >= currentParticipantCount && !label?.includes("200+");
    });
    if (firstValidTier && selectedParticipants !== firstValidTier) {
      const isCurrentDisabled = disabledParticipantTiers.has(selectedParticipants);
      if (!selectedParticipants || isCurrentDisabled) setSelectedParticipants(firstValidTier);
    }
  }, [isCurrentSubscription, currentParticipantCount, pricingTableByplan?.participants, disabledParticipantTiers, selectedParticipants]);

  const handlechangePrice = (eventType, data) => {
    if (data && eventType === "noOfParticipants") {
      setSelectedParticipants(data);
      setSelectedPrice(null);
    } else if (eventType === "price" && Object.keys(data).length > 0) {
      const keyName = Object.keys(data)?.[0] || null;
      if (keyName !== null) {
        setSelectedParticipants(keyName);
        setSelectedPrice(data);
      }
    }
  };

  /** Participant tier label for current plan (e.g. "21 to 50 Participants") — used to show "Current" in table. */
  const currentPlanParticipantLabel =
    isCurrentSubscription?.noOfParticipants?.replace(":", "")?.trim() ?? null;

  const canProceedToCheckout =
    selectedPrice && selectedParticipants && checkoutPage && currentSelectedPlan;

  if (canProceedToCheckout) {
    return (
      <CheckoutWrapper
        handleBack={() => setCheckoutPage(false)}
        data={{
          selectedPrice: selectedPrice?.[selectedParticipants] ?? null,
          selectedParticipants,
          selectedPlan: `${getPlanTypeLabel(currentSelectedPlan)} `,
          hasCurrentSubscription,
        }}
        paymentSettings={paymentSettings}
        defaultCardDetails={defaultCardDetails}
        stripeDefaultPaymentMethodLaoder={stripeDefaultPaymentMethodLaoder}
        handleCloseCheckoutPage={handleCloseCheckoutPage}
      />
    );
  }

  return (
    <div className='w-full px-4 py-2'>
      <div className='mb-5 border-gray-200 px-4 pt-2'>
        <Breadcrumb
          pages={BILLING_BREADCRUMB(BILLING_BREADCRUMB_PAGES.BILLING_PLAN)}
        />
      </div>
      <div className='py-4 px-6'>
        <div>
          <h1 className='font-black text-2xl'>Pricing</h1>
        </div>
        <div className='py-4 text-sm text-gray-600'>
          <p>
          Our flexible pricing suits all needs, from startups to large participant bases. Save 25% with annual upfront payments.
          </p>
          <p>
          No contracts or cancellation fees—just notify us 14 days in advance.
          </p>

          {/* Current plan: explicit indication when user has an active subscription */}
          {isCurrentSubscription != null &&
            isCurrentSubscription?.subscriptionPlan &&
            (isCurrentSubscription?.noOfParticipants || isCurrentSubscription?.duration) && (
              <div className='mt-4 rounded-lg border border-[#2f4778] bg-white px-4 py-3 shadow-sm'>
                <p className='text-xs font-medium uppercase tracking-wide text-[#2f4778]'>
                  Your current plan
                </p>
                <p className='mt-1 text-base font-semibold text-gray-900'>
                  {[isCurrentSubscription.subscriptionPlan, isCurrentSubscription.noOfParticipants]
                    .filter(Boolean)
                    .join(" – ")}
                  {isCurrentSubscription.billingInterval && (
                    <span className='font-normal text-gray-600'>
                      {" "}
                      · {isCurrentSubscription.billingInterval}
                    </span>
                  )}
                </p>
                {isCurrentSubscription.duration && (
                  <p className='mt-0.5 text-sm text-gray-500'>
                    {isCurrentSubscription.duration}
                  </p>
                )}
              </div>
            )}

          {currentParticipantCount > 0 && (
            <div className='mt-4 rounded-lg border-l-4 border-[#2f4778] bg-[#eff6ff] px-4 py-3 shadow-sm'>
              <p className='text-base font-semibold text-[#1e3a5f]'>
                Your current usage: <strong className='text-[#2f4778]'>{currentParticipantCount} active participant{currentParticipantCount !== 1 ? "s" : ""}</strong>
              </p>
              <p className='mt-1 text-sm text-[#334155]'>
                Choose a plan that supports at least this many participants.
              </p>
            </div>
          )}
        </div>
        <div className='w-full flex justify-between'>
          <div className='w-[30%]'>
            <div className={classNames(
              "w-full grid border border-1 border-gray-300 rounded-full grid-cols-3"
            )}>
              {[PLAN_TYPE.TOTAL_SUITE, PLAN_TYPE.ELEVATED, PLAN_TYPE.PLATINUM_PLAN].map(
                (planType) => (
                  <span
                    key={planType}
                    onClick={() => setCurrentSelectedPlan(planType)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setCurrentSelectedPlan(planType);
                      }
                    }}
                    className={classNames(
                      "transition duration-300 col-span-1 rounded-full flex items-center justify-center py-2.5 cursor-pointer text-sm sm:text-base",
                      currentSelectedPlan === planType
                        ? "bg-[#2f4778] text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    )}
                  >
                    {getPlanTypeLabel(planType)}
                  </span>
                )
              )}
            </div>
          </div>
          {/*<div>
            {
              isShowXeroButton && 
            <CustomButton label="Pay through Xero" clickHandler={()=>window.open(`https://apps.xero.com/subscribe/${XERO_APP_ID}`, "_blank")} type="button"  variant={null} cssClass="bg-green-100 border border-green-500 text-green-600 rounded-full" />
            }
          </div>*/}
        </div>
        <div className='pt-4'>
          {!pricingTableByplan && (!data || data.length === 0) ? (
            <div className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-800'>
              <p className='font-medium'>No plans available</p>
              <p className='mt-1'>
                In local development, pricing comes from Stripe. Add products named{" "}
                <strong>Total Suite</strong>, <strong>Elevated</strong>, and{" "}
                <strong>Platinum Plan</strong> in your Stripe Dashboard (Products)
                with prices; use the same nickname format as production so the
                table parses correctly.
              </p>
              <p className='mt-2 text-amber-700'>
                Or ensure <code className='rounded bg-amber-100 px-1'>STRIPE_SECRET_KEY</code> in
                backend <code className='rounded bg-amber-100 px-1'>.env</code> is set and points to
                a Stripe account that has these products.
              </p>
            </div>
          ) : (
          <div className='grid grid-cols-3 gap-x-4 items-stretch'>
            <div className='col-span-1 min-h-0 flex'>
              <PricingItemCard
                selectedData={{
                  selectedPrice,
                  selectedParticipants,
                }}
                renderFor='participants'
                data={pricingTableByplan?.participants ?? []}
                handleChange={handlechangePrice}
                isSubscriptionNotChangeable={false}
                disabledParticipantTiers={disabledParticipantTiers}
                downgradeDisabledTiers={downgradeDisabledTiers}
                currentParticipantCount={currentParticipantCount}
                currentPlanParticipantLabel={currentPlanParticipantLabel}
              />
            </div>
            <div className='col-span-1 min-h-0 flex'>
              <PricingItemCard
                selectedData={{
                  selectedPrice: selectedPrice?.[selectedParticipants] ?? null,
                  selectedParticipants,
                }}
                renderFor='year_1'
                data={pricingTableByplan?.year_1 ?? {}}
                handleChange={handlechangePrice}
                isSubscriptionNotChangeable={false}
                disabledParticipantTiers={disabledParticipantTiers}
                downgradeDisabledTiers={downgradeDisabledTiers}
                currentParticipantCount={currentParticipantCount}
                currentPlanPriceId={isCurrentSubscription?.id}
                currentPlanParticipantLabel={currentPlanParticipantLabel}
              />
            </div>
            <div className='col-span-1 min-h-0 flex'>
              <PricingItemCard
                selectedData={{
                  selectedPrice: selectedPrice?.[selectedParticipants] ?? null,
                  selectedParticipants,
                }}
                renderFor='yearly'
                data={pricingTableByplan?.yearly ?? {}}
                monthlyPriceData={pricingTableByplan?.year_1 ?? {}}
                handleChange={handlechangePrice}
                isSubscriptionNotChangeable={false}
                disabledParticipantTiers={disabledParticipantTiers}
                downgradeDisabledTiers={downgradeDisabledTiers}
                currentParticipantCount={currentParticipantCount}
                currentPlanPriceId={isCurrentSubscription?.id}
                currentPlanParticipantLabel={currentPlanParticipantLabel}
              />
            </div>
          </div>
          )}
        </div>
        <div className='py-8'>
          <div className='w-full flex justify-center items-center'>
            <CustomButton
              isDisabled={
                !selectedPrice ||
                !selectedParticipants ||
                (hasCurrentSubscription &&
                  selectedPrice?.[selectedParticipants]?.price_id === isCurrentSubscription?.id)
              }
              clickHandler={() => {
                const priceObj = selectedPrice?.[selectedParticipants];
                const maxAllowed = priceObj?.participant_max != null ? Number(priceObj.participant_max) : null;
                if (currentParticipantCount > 0 && maxAllowed != null && currentParticipantCount > maxAllowed) {
                  toast.error(
                    `This plan supports up to ${maxAllowed} participants. You have ${currentParticipantCount}. Please choose a higher tier from the table above.`,
                    { autoClose: 6000 }
                  );
                  return;
                }
                setConfirmPlanModalOpen(true);
              }}
              label={
                hasCurrentSubscription
                  ? "Change plan"
                  : "Subscribe plan"
              }
              cssClass='rounded-full flex justify-center items-center w-[20%] py-3'
            />
          </div>
        </div>

        {/* Confirmation modal before checkout */}
        <ModalSkeleton
          isOpen={confirmPlanModalOpen}
          closeModal={() => setConfirmPlanModalOpen(false)}
          title={
            hasCurrentSubscription
              ? "Confirm plan change"
              : "Confirm subscription"
          }
          cssClass='w-full max-w-md z-[99999]'
          bodyPadding='p-4'
          modalFooter={
            <div className='flex justify-end gap-x-2 w-full'>
              <CustomButton
                type='button'
                label='Cancel'
                variant='secondary'
                clickHandler={() => setConfirmPlanModalOpen(false)}
              />
              <CustomButton
                type='button'
                label='Continue to payment'
                clickHandler={() => {
                  setConfirmPlanModalOpen(false);
                  setCheckoutPage(true);
                }}
              />
            </div>
          }
        >
          {selectedPlanSummary && (
            <div className='space-y-4'>
              <p className='text-gray-700'>
                {hasCurrentSubscription ? (
                  <>
                    You are about to <strong>change your subscription</strong>.
                    The new plan will replace your current plan. You may see a
                    one-off charge or credit on your card based on the switch
                    date—see below. Please review the details before continuing.
                  </>
                ) : (
                  <>
                    You are about to <strong>subscribe</strong> to the following
                    plan. You will be taken to the payment page to complete the
                    purchase.
                  </>
                )}
              </p>

              {hasCurrentSubscription && (
                <div className='rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 text-sm text-gray-700'>
                  <p className='font-medium text-gray-800 mb-1'>What happens to your billing</p>
                  <p className='text-gray-600'>
                    You get credit for the unused part of your current plan, and you’re charged for the new plan from today. That amount is charged to your saved card straight away. You’ll see it under <strong>Subscription Details → Invoices</strong> or on your card statement.
                  </p>
                </div>
              )}

              {hasCurrentSubscription && (
                <div className='rounded-lg border border-gray-200 bg-gray-100 p-4'>
                  <p className='text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3'>
                    Current plan
                  </p>
                  <dl className='space-y-2 text-sm'>
                    <div className='flex justify-between gap-x-4'>
                      <dt className='text-gray-600'>Plan</dt>
                      <dd className='font-medium text-gray-900'>
                        {isCurrentSubscription.subscriptionPlan ?? "—"}
                      </dd>
                    </div>
                    <div className='flex justify-between gap-x-4'>
                      <dt className='text-gray-600'>Participant tier</dt>
                      <dd className='font-medium text-gray-900'>
                        {isCurrentSubscription.noOfParticipants ?? "—"}
                      </dd>
                    </div>
                    <div className='flex justify-between gap-x-4'>
                      <dt className='text-gray-600'>Billing</dt>
                      <dd className='font-medium text-gray-900'>
                        {isCurrentSubscription.billingInterval ?? "—"}
                      </dd>
                    </div>
                    {isCurrentSubscription.duration && (
                      <div className='flex justify-between gap-x-4'>
                        <dt className='text-gray-600'>Price</dt>
                        <dd className='font-medium text-gray-900'>
                          {isCurrentSubscription.duration}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              <div className='rounded-lg border-2 border-[#2f4778] bg-[#eff6ff] p-4'>
                <p className='text-xs font-semibold uppercase tracking-wide text-[#2f4778] mb-3'>
                  {hasCurrentSubscription ? "New plan" : "Plan details"}
                </p>
                <dl className='space-y-2 text-sm'>
                  <div className='flex justify-between gap-x-4'>
                    <dt className='text-gray-600'>Plan</dt>
                    <dd className='font-medium text-gray-900'>
                      {selectedPlanSummary.planTypeLabel}
                    </dd>
                  </div>
                  <div className='flex justify-between gap-x-4'>
                    <dt className='text-gray-600'>Participant tier</dt>
                    <dd className='font-medium text-gray-900'>
                      {selectedPlanSummary.participantTier}
                    </dd>
                  </div>
                  <div className='flex justify-between gap-x-4'>
                    <dt className='text-gray-600'>Billing</dt>
                    <dd className='font-medium text-gray-900'>
                      {selectedPlanSummary.billingLabel}
                    </dd>
                  </div>
                  <div className='flex justify-between gap-x-4'>
                    <dt className='text-gray-600'>Price</dt>
                    <dd className='font-medium text-gray-900'>
                      {selectedPlanSummary.priceDisplay}
                    </dd>
                  </div>
                  {!hasCurrentSubscription && (onboardingFeeLoading ? (
                    <div className='flex justify-between gap-x-4'>
                      <dt className='text-gray-600'>Onboarding (one-time)</dt>
                      <dd className='font-medium text-gray-500 text-sm'>Loading…</dd>
                    </div>
                  ) : onboardingFee ? (
                    <div className='flex justify-between gap-x-4'>
                      <dt className='text-gray-600'>{onboardingFee.label || "Onboarding (one-time)"}</dt>
                      <dd className='font-medium text-gray-900'>
                        ${Number(onboardingFee.amount ?? 0).toFixed(2)}
                      </dd>
                    </div>
                  ) : null)}
                </dl>
              </div>
            </div>
          )}
        </ModalSkeleton>

        <div className='py-4'>
          <div
            className={classNames(
              "px-10 mx-10 pt-2 grid grid-cols-6 font-extrabold text-md gap-x-2 border-t-2 border-gray-200"
            )}
          >
            <div className='col-span-3'>Features</div>
            <div className='col-span-1 text-center'>Total Suite</div>
            <div className='col-span-1 text-center'>Elevated</div>
            <div className='col-span-1 text-center'>Platinum</div>
          </div>
          {Object.keys(features).map((key) => (
            <React.Fragment key={key}>
              <div className='mx-10 px-1 py-2 m-2 border-t-2 border-b-2 border-gray-200 bg-gray-300 font-extrabold text-md'>
                {key}
              </div>
              {features[key].map((item, idx) => {
                const renderTierCell = (tierKey) => {
                  if (Object.prototype.hasOwnProperty.call(item, tierKey)) {
                    const tierValue = item[tierKey];
                    if (typeof tierValue === "string") {
                      return (
                        <span className='text-xs font-medium text-gray-800'>
                          {tierValue}
                        </span>
                      );
                    }
                    return tierValue ? (
                      <CheckCircleIcon height={20} width={20} color='green' />
                    ) : (
                      <BsDash
                        color='red'
                        style={{ width: "25px", height: "auto" }}
                      />
                    );
                  }
                  if (tierKey === "total_suite") {
                    return item.isBasicPlan === false ? (
                      <BsDash
                        color='red'
                        style={{ width: "25px", height: "auto" }}
                      />
                    ) : (
                      <CheckCircleIcon height={20} width={20} color='green' />
                    );
                  }
                  if (tierKey === "elevated") {
                    const elevatedIncluded =
                      item.isBasicPlan === true && item.isPlatinumPlan === true;
                    return elevatedIncluded ? (
                      <CheckCircleIcon height={20} width={20} color='green' />
                    ) : (
                      <BsDash
                        color='red'
                        style={{ width: "25px", height: "auto" }}
                      />
                    );
                  }
                  if (tierKey === "platinum_plan") {
                    return item.isPlatinumPlan === false ? (
                      <BsDash
                        color='red'
                        style={{ width: "25px", height: "auto" }}
                      />
                    ) : (
                      <CheckCircleIcon height={20} width={20} color='green' />
                    );
                  }
                  return (
                    <BsDash
                      color='red'
                      style={{ width: "25px", height: "auto" }}
                    />
                  );
                };

                return (
                  <div
                    key={idx}
                    className={`px-10 grid grid-cols-6 gap-x-2 text-sm py-3 ${
                      idx % 2 === 0 ? "bg-gray-50" : ""
                    }`}
                  >
                    <div className='col-span-3 px-2 flex justify-start gap-x-2 items-center'>
                      {item.value}
                    </div>
                    <div className='col-span-1 text-center flex justify-center items-center'>
                      {renderTierCell("total_suite")}
                    </div>
                    <div className='col-span-1 text-center flex justify-center items-center'>
                      {renderTierCell("elevated")}
                    </div>
                    <div className='col-span-1 text-center flex justify-center items-center'>
                      {renderTierCell("platinum_plan")}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className='py-6'>
          <div className='pb-6'>
            <span
              className='font-black text-black text-2xl '
              style={{ fontWeight: "1000" }}
            >
              Terms & Conditions*
            </span>
          </div>
          <div className='pl-1 space-y-2.5 text-sm'>
            <div className='col-span-3 flex justify-start gap-x-2 items-center'>
              <span>•</span>
              Pricing is indicative only and at the absolute discretion of the
              Software Vendor.
            </div>
            <div className='col-span-3 flex justify-start gap-x-2 items-center'>
              <span>•</span>
              To avail complimentary benefits and rewards, you must have signed
              up for a period of one year.
            </div>
            <div className='col-span-3 flex justify-start gap-x-2 items-center'>
              <span>•</span>
              To avail complimentary benefits , you must have signed up for a
              Platinum Plan.
            </div>
            <div className='col-span-3 flex justify-start gap-x-2 items-center'>
              <span>•</span>
              Annual upfront plans offer discounted pricing and are subject to
              exit fees if terminated early. See our Terms & Conditions for full
              details.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTableNew;
